export const filterGeojsonEntitiesByProperty = async (viewer, dataSource, filters = []) => {
  if (!dataSource || !dataSource.entities) return;

  const entities = dataSource.entities.values;
  const matchingEntities = [];

  // Try to convert value to number if possible
  const tryParseNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? val : num;
  };

  // Compare two values with operator
  const compare = (a, b, operator) => {
    a = tryParseNumber(a);
    b = tryParseNumber(b);

    switch (operator) {
      case "==":
        return a == b;
      case "!=":
        return a != b;
      case "<":
        return a < b;
      case "<=":
        return a <= b;
      case ">":
        return a > b;
      case ">=":
        return a >= b;
      default:
        return false;
    }
  };

  // Read value by dotted path (supports Cesium properties, nested objects, arrays)
  const getPropValueByPath = (props, keyPath) => {
    if (!props || !keyPath) return undefined;

    const segments = String(keyPath).split(".");
    const [rootKey, ...rest] = segments;

    let prop = props[rootKey];

    // Fallback: some PropertyBags expose getValue(time) returning plain object
    if (prop === undefined && typeof props.getValue === "function") {
      try {
        const time = viewer?.clock?.currentTime;
        const bagValue = props.getValue(time);
        if (bagValue && typeof bagValue === "object") {
          prop = bagValue[rootKey];
        }
      } catch (e) {
        try {
          const bagValue = props.getValue();
          if (bagValue && typeof bagValue === "object") {
            prop = bagValue[rootKey];
          }
        } catch {
          // ignore
        }
      }
    }

    if (prop === undefined) return undefined;

    let current;

    // Handle Cesium Property (ConstantProperty, etc.)
    if (prop && typeof prop.getValue === "function") {
      try {
        const time = viewer?.clock?.currentTime;
        current = prop.getValue(time);
      } catch (e) {
        try {
          current = prop.getValue();
        } catch {
          current = prop;
        }
      }
    } else {
      current = prop;
    }

    // Traverse nested path on plain JS value
    for (const segment of rest) {
      if (current == null) return undefined;

      if (Array.isArray(current)) {
        // Use first object-like element (same rule as extractNestedKeys)
        const firstObj = current.find((el) => el && typeof el === "object");
        if (!firstObj) return undefined;
        current = firstObj[segment];
      } else if (typeof current === "object") {
        current = current[segment];
      } else {
        return undefined;
      }
    }

    return current;
  };

  // Evaluate single filter for an entity
  const evaluateFilter = (filter, props) => {
    const { key, value, operation } = filter;
    if (!key) return false;
    if (!props) return false;

    const propVal = getPropValueByPath(props, key);
    if (propVal === undefined) return false;

    return compare(propVal, value, operation);
  };

  for (const entity of entities) {
    const props = entity.properties;
    let match = false;

    if (props && Array.isArray(filters) && filters.length > 0) {
      let result = null;
      let hasAnyFilter = false;

      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];

        const condition = evaluateFilter(filter, props);

        if (!hasAnyFilter) {
          result = condition;
          hasAnyFilter = true;
        } else {
          const op = (filter.logicalOp || "AND").toUpperCase();

          if (op === "OR") {
            result = result || condition;
          } else {
            result = result && condition;
          }
        }
      }

      match = hasAnyFilter ? result : false;
    } else {
      match = false;
    }

    entity.show = match;
    if (match) matchingEntities.push(entity);
  }

  if (matchingEntities.length > 0 && viewer) {
    await viewer.zoomTo(matchingEntities);
  }
};

export const clearLayerFilters = (layer, mapObjects, setMapObjects, resetEntityFilterFn) => {
  if (!layer) return;

  if (layer.dataSource) {
    resetEntityFilterFn(layer.dataSource);
  }

  const updated = [...mapObjects];
  for (const i in updated) {
    if (updated[i].id === layer.id) {
      updated[i].filters = undefined;
      break;
    }
  }
  setMapObjects(updated);
};

export function applyGeojsonLayerFilters({
  viewer,
  dataSource,
  filters,
  layerId,
  setMapObjects,
  setFilterGeojsonDialog,
  closeDialog = true,
}) {
  if (!viewer || !dataSource || !Array.isArray(filters) || filters.length === 0) return false;

  filterGeojsonEntitiesByProperty(viewer, dataSource, filters);

  if (typeof setMapObjects === "function" && layerId) {
    setMapObjects((prev) =>
      prev.map((o) => (o.id === layerId ? { ...o, filters } : o))
    );
  }

  if (closeDialog && typeof setFilterGeojsonDialog === "function") {
    setFilterGeojsonDialog(false);
  }

  return true;
}
